import {Component, ElementRef, Inject, Input, OnDestroy, OnInit, PLATFORM_ID, ViewChild} from '@angular/core';
import {Store} from '@ngrx/store';
import {Observable} from 'rxjs';
import {ContactAddressInterface, ContactOutputInterface, ContactPagedResponse, PutContactModel} from '@app/marketplace/checkout/_models/contact.model';
import {PaginatorComponent} from '@app/marketplace/shared/_components/paginator.component';
import {ActivatedRoute, Router} from '@angular/router';
import {Meta, Title} from '@angular/platform-browser';
import {PaginationSelectorModel} from '@app/marketplace/shared/_models/pagination-selector.model';
import * as selectors from '@app/marketplace/checkout/state/contacts/contacts-selectors';
import * as selectorsLocation from '@app/marketplace/locations/state/locations-selectors';
import * as selectorsCheckout from '@app/marketplace/checkout/state/checkout/checkout-selectors';
import * as contactsActions from '@app/marketplace/checkout/state/contacts/contacts-actions';
import * as checkoutActions from '@app/marketplace/checkout/state/checkout/checkout-actions';
import * as fromShoppingCartState from '@app/marketplace/products/state/shopping-cart/cart-state';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {LocationTypeEnum} from '@app/marketplace/shared/_models/location.model';
import {isPlatformBrowser} from '@angular/common';
import {initProvinces} from '@app/marketplace/locations/locations';
import * as locationState from '@app/marketplace/locations/state/locations-state';
import {CheckoutInterface} from '@app/marketplace/checkout/_models/checkout.model';
import * as cartSelectors from '@app/marketplace/products/state/shopping-cart/cart-selectors';
import * as locationsActions from '@app/marketplace/locations/state/locations-actions';
import {DeviceDetectorService} from 'ngx-device-detector';

@Component({
  selector: 'app-contacts',
  templateUrl: './contacts.component.html',
  styleUrls: ['./contacts.component.css'],
  providers: [DeviceDetectorService]
})
export class ContactsComponent extends PaginatorComponent<ContactOutputInterface> implements OnInit, OnDestroy {
  @ViewChild('selectItems', {static: false})
  selectItems: ElementRef;
  @Input()
  firstContact: boolean;
  @Input()
  selectLocation: boolean;
  payload: any;
  contacts: FormArray;
  selectedContact$: Observable<ContactPagedResponse>;
  selectUpdating$: Observable<boolean>;
  locations = initProvinces;
  location: number = null;
  showAddContactForm = false;
  contactToEdit: ContactOutputInterface;
  checkoutRequest: any;

  selection = false;
  addingByDefault = false;
  showOptions = '';

  constructor(private route: ActivatedRoute,
              private metaService: Meta,
              private titleService: Title,
              private router: Router,
              private store: Store<any>,
              public modelService: NgbActiveModal,
              private deviceService: DeviceDetectorService,
              @Inject(PLATFORM_ID) private platformId) {
    super(route, metaService, titleService, router, store, platformId, new PaginationSelectorModel(
      selectors.selectLoading,
      selectors.selectItems,
      selectors.selectPageSize,
      selectors.selectPageNo,
      selectors.selectTotalPage,
      selectors.selectTotalData,
      selectors.selectCount
    ));
    this.contacts = new FormArray([]);
    this.payload = {
      pageIndex: 1,
      pageSize: 5,
      pagingStrategy: 0,
    };
    if (isPlatformBrowser(platformId)) {
      this.selectedContact$ = this.store.select(selectors.selectResponse);
      this.selectUpdating$ = this.store.select(selectors.selectUpdating);
    }
    this.checkoutRequest = {
      contactId: '',
      signature: '',
      items: {}
    };
  }

  ngOnDestroy(): void {
    this.store.dispatch(contactsActions.CleaningContacts());
    this.subscriptions.forEach(x => x.unsubscribe());
  }

  ngOnInit(): void {
    this.showAddContactForm = !this.firstContact;
    this.selectUpdating$.subscribe((result) => {
      if (result == false && (this.selection || this.addingByDefault)) {
        this.selection = false;
        this.addingByDefault = false;
        this.modelService.close({loadCheckout: true});
      }
    });
    this.loadContacts();
  }

  onChange($event) {
    document.body.click();
    this.payload.locations = this.location ? [this.location] : [];
    this.payload.locationType = initProvinces.find(province => province.value == this.location) ?
      LocationTypeEnum.Province : LocationTypeEnum.Municipality;
    this.payload.customLocation = true;
    this.loadContacts();
  }

  getCheckBoxControl = (code: string): FormGroup => this.contacts.controls.find((contact: FormGroup) => contact.controls['code'].value === code) as FormGroup;

  loadContacts = () => {
    if (!this.payload.pageIndex) {
      this.payload.pageIndex = 1;
    }
    if (!this.payload.pageSize) {
      this.payload.pageIndex = 10;
    }
    this.store.dispatch(contactsActions.LoadingContacts({payload: this.payload}));
  };

  removeContacts = (contact: ContactOutputInterface) => {
    this.payload.customLocation = true;
    this.payload.locations = this.location ? [this.location] : [];
    const payload = {
      payload: this.payload,
      codes: [{code: contact.code, isDefault: contact.isDefault}]
    };
    this.store.dispatch(contactsActions.RemovingContact(payload));
  };

  cancelRequest = () => this.store.dispatch(contactsActions.CancelContactsRequest());
  pageChange = ($event: number) => {
    this.payload.pageIndex = $event;
    this.loadContacts();
  };

  formatAddress(address: ContactAddressInterface, town: string = '') {
    let result = address.addressLine1;
    if (address.addressLine2) {
      result += `, ${address.addressLine2}`;
    }
    if (town) {
      result += `, ${town}`;
    }
    if (address.city) {
      result += `, ${address.city}`;
    }
    if (address.state) {
      result += `, ${address.state}`;
    }
    if (address.zipCode) {
      result += `, ${address.zipCode}`;
    }
    if (address.country) {
      result += `, ${address.country}`;
    }
    return result;
  }

  selectItem(contact: ContactOutputInterface) {
    this.selection = true;
    const model = new PutContactModel(contact);
    model.addressLine1 = contact.address.addressLine1;
    model.addressLine2 = contact.address.addressLine2;
    model.zipCode = contact.address.zipCode;
    model.location = contact.municipality;
    model.isDefault = true;
    this.store.dispatch(contactsActions.SelectingDefaultContact({data: model}));
    if (this.selectLocation) {
      this.store.dispatch(locationsActions.UpdatedLocation({locationType: LocationTypeEnum.Municipality, locations: [contact.municipality]}));
    }
    this.checkoutRequest.contactId = model.code;
  }

  showForm($event) {
    this.payload.customLocation = true;
    this.payload.locations = this.location ? [this.location] : [];
    this.showAddContactForm = $event.showForm;
    const payload = {
      payload: this.payload,
      data: $event.contact
    };
    if ($event.contact && $event.action == 'update') {
      this.store.dispatch(contactsActions.UpdatingContact(payload));
    } else if ($event.contact && $event.action == 'add') {
      this.addingByDefault = $event.contact.isDefault;
      this.store.dispatch(contactsActions.AddingContact(payload));
      if ($event.contact.isDefault) {
        this.modelService.close({loadCheckout: true});
      }
    }
  }

  options(contact: ContactOutputInterface) {
    if (this.deviceService.isMobile() || this.deviceService.isTablet()) {
      this.showOptions = contact.code;
    } else {
      this.selectItem(contact);
    }
  }
}
